import { colors } from '@cabonline/ui-shared';
import { Pill, Text } from '@cabonline/ui-web';
import {
  PassengerDetailsStatus,
  PassengerStatus,
  useLocale,
} from '@multiplatform-apps/service-trips';
import { FC } from 'react';

interface StatusProps {
  status: PassengerStatus | PassengerDetailsStatus;
  truncate?: boolean;
}

const passengerStatusMapper: Record<
  PassengerDetailsStatus | PassengerStatus,
  string
> = {
  COMPLETED: 'status.pill.completed',
  INACTIVE: 'status.pill.inactive',
  ACTIVE: 'status.pill.active',
  AWAITING_APPROVAL: 'status.pill.awaitingApproval',

  // Support old status-format
  [PassengerStatus.Completed]: 'status.pill.completed',
  [PassengerStatus.Inactive]: 'status.pill.inactive',
  [PassengerStatus.Active]: 'status.pill.active',
  [PassengerStatus.AwaitingApproval]: 'status.pill.awaitingApproval',
};

export const getPassengerStatusCopyKey = (
  status: PassengerDetailsStatus | PassengerStatus
) => passengerStatusMapper[status];

export const StatusPill: FC<StatusProps> = ({ status, truncate = false }) => {
  const { t } = useLocale();

  switch (status) {
    case 'COMPLETED':
      return (
        <Pill truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            title={t('status.pill.completed')}
            weight="extraBold"
          >
            {t('status.pill.completed')}
          </Text>
        </Pill>
      );
    case 'INACTIVE':
      return (
        <Pill truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.inactive')}
          >
            {t('status.pill.inactive')}
          </Text>
        </Pill>
      );
    case 'ACTIVE':
      return (
        <Pill backgroundColor={colors.blue} truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.active')}
          >
            {t('status.pill.active')}
          </Text>
        </Pill>
      );
    case 'AWAITING_APPROVAL':
      return (
        <Pill backgroundColor={colors.gray2} truncate={truncate}>
          <Text
            color={colors.white}
            size="s"
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.awaitingApproval')}
          >
            {t('status.pill.awaitingApproval')}
          </Text>
        </Pill>
      );

    // Support legacy numeric "state" vars
    case PassengerStatus.Completed:
      return (
        <Pill truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.completed')}
          >
            {t('status.pill.completed')}
          </Text>
        </Pill>
      );
    case PassengerStatus.Inactive:
      return (
        <Pill truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.inactive')}
          >
            {t('status.pill.inactive')}
          </Text>
        </Pill>
      );
    case PassengerStatus.Active:
      return (
        <Pill backgroundColor={colors.blue} truncate={truncate}>
          <Text
            size="s"
            color={colors.white}
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.active')}
          >
            {t('status.pill.active')}
          </Text>
        </Pill>
      );
    case PassengerStatus.AwaitingApproval:
      return (
        <Pill backgroundColor={colors.gray2} truncate={truncate}>
          <Text
            color={colors.white}
            size="s"
            truncate={truncate}
            weight="extraBold"
            title={t('status.pill.awaitingApproval')}
          >
            {t('status.pill.awaitingApproval')}
          </Text>
        </Pill>
      );
    default:
      return null;
  }
};
